let _ = t => t,
  _t,
  _t2;
import { useState } from 'react';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { Text } from 'foundations-components/components/Text';
import { Spacer } from 'foundations-components/components/Spacer';
import { View } from 'foundations-components/components/View';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { DateIcon } from 'foundations-assets/icons/components/DateIcon';
import { ShareIcon } from 'foundations-assets/icons/components/ShareIcon';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import { Role } from '../../components/Role';
import { SocialLinks } from '../../components/SocialLinks';
import { cssWithTheme, useMediaQuery } from '../../../../utils/Styling';
import { ContentWrapper } from '../../components/ContentWrapper';
import { MakeProfilePublicModal } from './MakeProfilePublicModal';
import { generatePublicProfileUrl } from '../../utils/profileUrls';
import { ProfileCardWrapper } from '../../components/ProfileCardWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ImageWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__ImageWrapper"
})(["display:flex;align-items:center;justify-content:center;grid-area:avatar;", ""], ({
  $isMobile,
  theme
}) => $isMobile && cssWithTheme(_t || (_t = _`
      justify-content: flex-start;
      padding-block-end: ${0};
    `), theme.space['300']));
const NameWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__NameWrapper"
})(["grid-area:name;"]);
const SocialWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__SocialWrapper"
})(["grid-area:social;"]);
const GridContainer = styled.div.withConfig({
  displayName: "ProfilePageHeader__GridContainer"
})(["display:grid;width:100%;grid-template-columns:auto auto 1fr;grid-template-areas:'avatar name social';gap:", ";", ""], ({
  theme
}) => theme.space['400'], ({
  $isMobile,
  theme
}) => $isMobile && cssWithTheme(_t2 || (_t2 = _`
      grid-template-columns: 1fr auto;
      grid-template-areas:
        'avatar'
        'name'
        'social';
      gap: ${0};
    `), theme.space['0']));
const ActionButtons = ({
  userProfile,
  editPath,
  onOpenModal,
  trackInteraction
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const publicUrl = generatePublicProfileUrl(userProfile.userId);
  return /*#__PURE__*/_jsxs(ButtonWrapper, {
    alignItems: "center",
    children: [userProfile.public ? /*#__PURE__*/_jsxs(CopyButton, {
      "data-test-id": "copy-button",
      onClick: () => {
        trackInteraction('share-profile');
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('profilePage.share.messages.success')
        });
      },
      size: isMobile ? 'medium' : 'small',
      use: "tertiary",
      value: publicUrl,
      children: [/*#__PURE__*/_jsx(ShareIcon, {
        size: "300"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.share.button.shareProfile"
      })]
    }) : /*#__PURE__*/_jsxs(Button, {
      "data-test-id": "open-mode-button",
      onClick: () => {
        onOpenModal();
      },
      size: isMobile ? 'medium' : 'small',
      use: "tertiary",
      children: [/*#__PURE__*/_jsx(ShareIcon, {
        size: "300"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.share.button.shareProfile"
      })]
    }), /*#__PURE__*/_jsx(ACRouterButton, {
      onClick: () => {
        trackInteraction('edit-profile');
      },
      size: isMobile ? 'medium' : 'small',
      to: editPath,
      use: "tertiary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.header.editProfileButton"
      })
    })]
  });
};
export const ProfilePageHeader = ({
  userProfile,
  hasEditAccess,
  editPath,
  trackInteraction,
  isHubless = false
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const [makePublicModalOpen, setMakePublicModalOpen] = useState(false);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ContentWrapper, {
      isHubless: isHubless,
      children: /*#__PURE__*/_jsx(ProfileCardWrapper, {
        children: /*#__PURE__*/_jsx(View, {
          paddingBlock: "600",
          paddingInline: "0",
          children: /*#__PURE__*/_jsxs(GridContainer, {
            $isMobile: isMobile,
            children: [/*#__PURE__*/_jsx(ImageWrapper, {
              $isMobile: isMobile,
              children: /*#__PURE__*/_jsx(Image, {
                inlineSize: isMobile ? '60px' : '80px',
                marginInlineEnd: "400",
                shape: "circle",
                src: userProfile.avatarUrl
              })
            }), /*#__PURE__*/_jsx(NameWrapper, {
              children: /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                children: [/*#__PURE__*/_jsxs(Flex, {
                  alignItems: isMobile ? 'start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  children: [/*#__PURE__*/_jsx(Text, {
                    as: "h1",
                    typography: isMobile ? 'heading-400' : 'heading-600',
                    children: userProfile.displayName
                  }), userProfile.pronouns && /*#__PURE__*/_jsx(Text, {
                    marginInlineStart: isMobile ? '0' : '400',
                    paddingBlockEnd: "200",
                    typography: isMobile ? 'detail-200' : 'heading-25',
                    children: userProfile.pronouns
                  })]
                }), /*#__PURE__*/_jsx(Role, {
                  companyName: userProfile.companyName,
                  jobTitle: userProfile.jobTitle
                }), userProfile.createdAt ? /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  paddingBlockStart: "200",
                  children: [/*#__PURE__*/_jsx(DateIcon, {}), /*#__PURE__*/_jsx(Spacer, {
                    display: "inline-block",
                    space: "200"
                  }), /*#__PURE__*/_jsx(Text, {
                    typography: isMobile ? 'detail-200' : 'heading-25',
                    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                      elements: {
                        Date: FormattedDateTime
                      },
                      message: "profilePage.profileHeader.joinedDate_jsx",
                      options: {
                        value: userProfile.createdAt,
                        format: 'short-m-y'
                      }
                    })
                  })]
                }) : null]
              })
            }), /*#__PURE__*/_jsx(SocialWrapper, {
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: isMobile ? undefined : 'end',
                flexDirection: "column",
                flexGrow: 1,
                children: [/*#__PURE__*/_jsx(SocialLinks, {
                  userProfile: userProfile
                }), hasEditAccess && /*#__PURE__*/_jsx(ActionButtons, {
                  editPath: editPath,
                  onOpenModal: () => setMakePublicModalOpen(true),
                  trackInteraction: trackInteraction,
                  userProfile: userProfile
                })]
              })
            })]
          })
        })
      })
    }), makePublicModalOpen && /*#__PURE__*/_jsx(MakeProfilePublicModal, {
      onClose: () => setMakePublicModalOpen(false),
      trackInteraction: trackInteraction,
      userProfile: userProfile
    })]
  });
};